import { useState } from 'react';
import { useForm } from 'react-final-form';

import { ReservationRequiredField } from '../../../generated/graphql';
import { ClientTeamMemberOption } from '../../../hooks/useClientClientTeamMemberSelector';
import { useClientRequiredFieldsAndPod } from '../../../hooks/useClientRequiredFieldsAndPod';
import { getGuestLevelFields } from '../../../utilities/guestFieldMapping';
import {
  parseNameFromQuery,
  sanitizeUserInput,
} from '../../../utilities/nameParser';
import { looksLikeDODID } from '../../../utilities/validators';
import CreateGuestModal, {
  CreatedGuestValues,
  CreateGuestFormValues,
} from '../../CreateGuestModal';
import ClientTeamMemberAutoComplete from '../../Roster/ClientTeamMemberAutoComplete';

interface ClientTeamMemberArrayFieldProps {
  arrayName: string;
  labelText?: string;
  placeholder?: string;
  disabled?: boolean;
  allowCreateGuest?: boolean;
}

export default function ClientTeamMemberArrayField({
  arrayName,
  labelText = 'Add guests',
  placeholder = 'Search by ID or Name',
  disabled = false,
  allowCreateGuest = false,
}: ClientTeamMemberArrayFieldProps) {
  const form = useForm();
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createGuestInitialValues, setCreateGuestInitialValues] = useState<
    Partial<CreateGuestFormValues>
  >({});
  // Use a key to force remount of ClientTeamMemberAutoComplete when needed
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);

  const guestRequiredFields = getGuestLevelFields(
    clientRequiredFields.map((field) => field.value),
  );

  const handleAdd = (guest: ClientTeamMemberOption | null) => {
    if (guest && form.mutators?.push) {
      // Destructure to exclude semiUniqueIdentifier and unknownGuest which are not allowed to be submitted to the backend
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { semiUniqueIdentifier, unknownGuest, ...guestForSubmission } =
        guest;
      form.mutators.push(arrayName, guestForSubmission);
    }
  };

  const openCreateGuestModal = (query: string) => {
    // Sanitize the input first
    const sanitizedQuery = sanitizeUserInput(query);

    // Parse the query to create initial values
    const initialValues: Partial<CreateGuestFormValues> = {};

    if (sanitizedQuery) {
      if (
        looksLikeDODID(sanitizedQuery) &&
        guestRequiredFields.includes(ReservationRequiredField.DodId)
      ) {
        // If it looks like a DoD ID and DoD ID is required, use it as edipi
        initialValues.edipi = sanitizedQuery;
      } else if (!looksLikeDODID(sanitizedQuery)) {
        // If it doesn't look like a DoD ID, parse it as a name
        const { firstName, lastName } = parseNameFromQuery(sanitizedQuery);
        initialValues.firstName = firstName;
        initialValues.lastName = lastName;
      }
    }

    setCreateGuestInitialValues(initialValues);
    setIsCreateModalOpen(true);
  };

  const handleGuestCreated = (guest: CreatedGuestValues) => {
    setIsCreateModalOpen(false);
    // Add the created guest to the array
    if (form.mutators?.push) {
      form.mutators.push(arrayName, guest);
    }
    // Force ClientTeamMemberAutoComplete to reset by changing its key
    setAutoCompleteKey((prev) => prev + 1);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setCreateGuestInitialValues({});
  };

  return (
    <>
      <ClientTeamMemberAutoComplete
        key={autoCompleteKey}
        labelText={labelText}
        placeholder={placeholder}
        onAdd={handleAdd}
        onCreateGuest={allowCreateGuest ? openCreateGuestModal : undefined}
        disabled={disabled}
      />
      {allowCreateGuest && (
        <CreateGuestModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          onGuestCreated={handleGuestCreated}
          initialValues={createGuestInitialValues}
        />
      )}
    </>
  );
}
